import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IoClose } from 'react-icons/io5';
import type { OtherNotificationItem } from '@th-types/worker-notifications.type';
import { formatLocalDate } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';
import defaultAvatar from '@assets/default-avatar.png';
import { TABLE_HEADER_STYLE } from '@components/InboxReviews/tableStyles';

interface OtherNotificationsTableProps {
  notifications: OtherNotificationItem[];
  onOpen: (notification: OtherNotificationItem) => void;
  onDismiss: (id: number) => void;
}

function OtherListItem({
  notification,
  onOpen,
  onDismiss,
}: {
  notification: OtherNotificationItem;
  onOpen: (notification: OtherNotificationItem) => void;
  onDismiss: (id: number) => void;
}) {
  const hasUnread = !notification.isRead;
  return (
    <Box py="sm" fw={hasUnread ? 700 : 400}>
      <Group align="flex-start" wrap="nowrap" gap="md">
        <Avatar
          src={notification.companyLogoUrl || defaultAvatar}
          size="md"
          radius="sm"
        />
        <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw="inherit">
            {notification.message}
          </Text>
          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              {notification.createdAt
                ? formatLocalDate(
                    notification.createdAt,
                    DATE_FORMATS.SHORT_DATE_YEAR_FORMAT
                  )
                : '-'}
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Button size="xs" fw={700} onClick={() => onOpen(notification)}>
                Go To
              </Button>
              <ActionIcon
                variant="default"
                onClick={() => onDismiss(notification.id)}
                aria-label="Dismiss notification"
              >
                <IoClose size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Stack>
      </Group>
    </Box>
  );
}

export default function OtherNotificationsTable({
  notifications,
  onOpen,
  onDismiss,
}: OtherNotificationsTableProps) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  if (!notifications.length) {
    return (
      <Text c="dimmed" ta="center" py="md" size="sm">
        No notifications.
      </Text>
    );
  }

  if (isMobile) {
    return (
      <Stack gap={0} w="100%">
        {notifications.map((notification, index) => (
          <div key={notification.id}>
            {index > 0 && <Divider />}
            <OtherListItem
              notification={notification}
              onOpen={onOpen}
              onDismiss={onDismiss}
            />
          </div>
        ))}
      </Stack>
    );
  }

  return (
    <Table highlightOnHover w="100%" style={{ tableLayout: 'fixed' }}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={TABLE_HEADER_STYLE} w={48} />
          <Table.Th style={TABLE_HEADER_STYLE}>Notification</Table.Th>
          <Table.Th style={TABLE_HEADER_STYLE} ta="right" w={180} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {notifications.map((notification) => {
          const hasUnread = !notification.isRead;
          const isHovered = hoveredId === notification.id;
          return (
            <Table.Tr
              key={notification.id}
              style={{ cursor: 'pointer' }}
              fw={hasUnread ? 700 : 400}
              onClick={() => onOpen(notification)}
              onMouseEnter={() => setHoveredId(notification.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <Table.Td w={48}>
                <Avatar
                  src={notification.companyLogoUrl || defaultAvatar}
                  size="md"
                  radius="sm"
                />
              </Table.Td>
              <Table.Td fz={14}>
                <Text
                  size="sm"
                  fw="inherit"
                  truncate
                  title={notification.message}
                >
                  {notification.message}
                </Text>
              </Table.Td>
              <Table.Td ta="right" fz={14}>
                <Flex align="center" justify="flex-end" gap={8} mih={40}>
                  {isHovered ? (
                    <>
                      <Button
                        size="compact-sm"
                        fw={700}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(notification);
                        }}
                      >
                        Go To
                      </Button>
                      <ActionIcon
                        variant="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDismiss(notification.id);
                        }}
                        aria-label="Dismiss notification"
                      >
                        <IoClose size={16} />
                      </ActionIcon>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {notification.createdAt
                        ? formatLocalDate(
                            notification.createdAt,
                            DATE_FORMATS.SHORT_DATE_YEAR_FORMAT
                          )
                        : '-'}
                    </Text>
                  )}
                </Flex>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
